import { useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { formatPrice, DEFAULT_CURRENCY, type PriceBreakdown } from '@printtat/shared';

interface PricingBreakdownProps {
  breakdown: PriceBreakdown;
  currency?: string;
  compact?: boolean;
}

const PricingBreakdown = ({ breakdown, currency = DEFAULT_CURRENCY, compact = false }: PricingBreakdownProps) => {
  const activeSurfaces = breakdown.surfaces.filter(
    (s) => s.hasDesign || s.optionDetails.length > 0,
  );

  const sz = compact ? 'text-[10px]' : 'text-[11px]';

  return (
    <div className="flex flex-col space-y-1.5">
      {/* ── Base product ── */}
      <div className={`flex justify-between ${sz} font-medium text-gray-700`}>
        <span>Base product</span>
        <span>{formatPrice(breakdown.basePrice, currency)}</span>
      </div>

      {/* ── Print setup ── */}
      {breakdown.hasPrint && breakdown.basePrintPrice > 0 && (
        <div className={`flex justify-between ${sz} font-medium text-gray-700`}>
          <span>Print setup</span>
          <span>{formatPrice(breakdown.basePrintPrice, currency)}</span>
        </div>
      )}

      {/* ── Per-surface breakdown ── */}
      {activeSurfaces.map((s) => (
        <div key={s.surface} className="space-y-0.5">
          <div className={`flex justify-between ${sz} font-medium text-gray-700`}>
            <span className="capitalize">
              {s.surface.replace(/([A-Z])/g, ' $1').trim()}
            </span>
            <span>{formatPrice(s.subtotal, currency)}</span>
          </div>

          {s.hasDesign && s.surfacePrice > 0 && (
            <Row label="Surface fee" value={formatPrice(s.surfacePrice, currency)} className={sz} muted />
          )}
          {s.hasDesign && s.coverageBracketLabel && (
            <Row
              label={`Coverage · ${s.coverageBracketLabel}`}
              value={s.coveragePrice > 0 ? formatPrice(s.coveragePrice, currency) : 'Incl.'}
              className={sz}
              muted
            />
          )}
          {s.optionDetails.map((opt) => (
            <Row
              key={opt.key}
              label={compact ? opt.valueLabel : `${opt.label}: ${opt.valueLabel}`}
              value={opt.price > 0 ? formatPrice(opt.price, currency) : 'Incl.'}
              className={sz}
              muted
            />
          ))}
        </div>
      ))}

      {/* ── Variant surcharge ── */}
      {breakdown.variantAdditionalPrice > 0 && (
        <div className={`flex justify-between ${sz} font-medium text-gray-700`}>
          <span>Size / colour</span>
          <span>{formatPrice(breakdown.variantAdditionalPrice, currency)}</span>
        </div>
      )}

      {/* ── Unit cost ── */}
      <div className="mt-3 flex justify-between items-center">
        <span className={`${sz} font-medium text-gray-500`}>Total cost:</span>
        <span className="flex items-center">
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-900`}>
            {formatPrice(breakdown.total, currency)}
          </span>
          <span className="text-[9px] text-gray-400 font-medium ml-1"> / unit</span>
        </span>
      </div>
    </div>
  );
};

/* ── Row helper ── */
interface RowProps {
  label: string;
  value: string;
  className?: string;
  muted?: boolean;
}

const Row = ({ label, value, className = '', muted = false }: RowProps) => (
  <div className={`flex justify-between ${muted ? 'text-gray-400' : 'text-gray-600'} ${className} leading-snug`}>
    <span>{muted && '- '}{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

/* ── PricingDetails — collapsible wrapper ── */

interface PricingDetailsProps {
  breakdown: PriceBreakdown;
  currency?: string;
  compact?: boolean;
  defaultOpen?: boolean;
  shadow?: boolean;
}

const PricingDetails = ({ breakdown, currency = DEFAULT_CURRENCY, compact = false, defaultOpen = true, shadow = true }: PricingDetailsProps) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`border w-full bg-gray-50 ${shadow ? 'shadow-lg' : ''} ${open ? 'rounded-lg' : 'rounded-2xl'}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="w-full flex items-center justify-between px-2.5 py-1.5 cursor-pointer"
      >
        <span className="flex items-center gap-1 shrink-0">
          <ChevronUp
            size={12}
            className={`text-gray-400 transition-transform ${open ? '' : 'rotate-180'}`}
          />
          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
            Pricing
          </span>
        </span>
        {!open && (
          <span className="text-[11px] whitespace-nowrap shrink-0">
            <span className="text-gray-700 font-semibold">{formatPrice(breakdown.total, currency)}</span>
            <span className="text-[9px] text-gray-400 font-medium"> / unit</span>
          </span>
        )}
      </button>
      {open && (
        <div className="px-2.5 pb-2">
          <PricingBreakdown breakdown={breakdown} currency={currency} compact={compact} />
        </div>
      )}
    </div>
  );
};

export { PricingBreakdown, PricingDetails };
export type { PricingBreakdownProps, PricingDetailsProps };
